import { useEffect, useRef, useState } from "react"
import { X } from "lucide-react"
import { useStudioStore } from "../../store/studio-store"
import { useUIBreakpoint } from "@/hooks/use-ui-breakpoint"
import { PropertyMetadataRegistry } from "../../components/properties/PropertyMetadataRegistry"
import { PropertiesPanel } from "@/features/preview/properties/properties-panel"

export function InspectorOverlay() {
  const isOpen = useStudioStore((s) => s.inspectorOverlayOpen)
  const componentId = useStudioStore((s) => s.activeComponentId)
  const position = useStudioStore((s) => s.inspectorPosition)
  const close = useStudioStore((s) => s.closeInspector)
  const setPosition = useStudioStore((s) => s.setInspectorPosition)
  // Phase 050 D-050-03a — body discriminator. 'abox' renders the new ABox
  // properties panel; 'metadata' falls through to the existing PR4
  // PropertyMetadataRegistry-driven body; null short-circuits with the
  // existing `!isOpen` early-return.
  const content = useStudioStore((s) => s.inspectorOverlayContent)
  // Phase 531 (SPEC §2.2 / RESP-03): at md the floating overlay is clamped to the
  // top-left and width-capped so it never overflows the narrow canvas; lg+ keeps
  // the draggable w-80 floating overlay. inspectorOverlayOpen stays canonical
  // (Pitfall 3 — no separate SPEC-only open flag is introduced here).
  const { bp } = useUIBreakpoint()
  const isCompact = bp === "md"

  const dragRef = useRef<{ startX: number; startY: number; origX: number; origY: number } | null>(null)
  const [tempPosition, setTempPosition] = useState<{ x: number; y: number } | null>(null)

  useEffect(() => {
    if (!isOpen) return
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") close()
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [isOpen, close])

  // Reset drag state on programmatic close (e.g. another source calls closeInspector
  // mid-drag). Without this, dragRef/tempPosition could be stale on next open.
  useEffect(() => {
    if (!isOpen) {
      dragRef.current = null
      setTempPosition(null)
    }
  }, [isOpen])

  function onPointerDown(e: React.PointerEvent<HTMLDivElement>) {
    dragRef.current = { startX: e.clientX, startY: e.clientY, origX: position.x, origY: position.y }
    e.currentTarget.setPointerCapture(e.pointerId)
  }
  function onPointerMove(e: React.PointerEvent<HTMLDivElement>) {
    if (!dragRef.current) return
    const dx = e.clientX - dragRef.current.startX
    const dy = e.clientY - dragRef.current.startY
    setTempPosition({ x: dragRef.current.origX + dx, y: dragRef.current.origY + dy })
  }
  function onPointerUp() {
    if (tempPosition) {
      setPosition(tempPosition)
      setTempPosition(null)
    }
    dragRef.current = null
  }

  if (!isOpen) return null
  // Phase 050 D-050-03a — the ABox panel path opens with
  // `activeComponentId === null` (the new `openAboxPropertiesPanel` action
  // doesn't set a component id; ABox identity lives in `previewSelection`).
  // The metadata path keeps the legacy short-circuit so the PR4 header's
  // `componentId.split('-')` is never invoked with a null id.
  if (content === 'metadata' && !componentId) return null
  // Defensive — if the overlay is open but no content discriminator is set
  // (legacy callers that opened the overlay without going through
  // `selectComponent` / `openAboxPropertiesPanel`), keep the prior behavior:
  // require a componentId to render.
  if (content === null && !componentId) return null

  const effectivePos = tempPosition ?? position
  // PR4 placeholder: derive component type from componentId prefix.
  // Production should look up via the component tree. Aliases below map common
  // shorthand IDs to registry keys.
  const TYPE_ALIASES: Record<string, string> = {
    btn: "button",
    txt: "text",
    cont: "container",
  }
  const rawType = componentId ? (componentId.split("-")[0] || "button") : "button"
  const componentType = TYPE_ALIASES[rawType] ?? rawType
  const metadata = PropertyMetadataRegistry.get(componentType)
  const properties = metadata?.properties ?? []
  // Phase 050 D-050-03a — drag-handle label swap. ABox path shows
  // "Properties"; metadata path shows the legacy "Inspector — {type}".
  const headerLabel = content === 'abox' ? 'Properties' : `Inspector — ${componentType}`

  // md: clamp to top-left + cap width so the overlay stays inside the narrow canvas
  // (no drag positioning); lg+: the existing draggable w-80 floating overlay.
  const panelClassName = isCompact
    ? "absolute left-2 top-2 max-w-[calc(100vw-1rem)] bg-card border border-border rounded-lg shadow-lg z-50 flex flex-col"
    : "absolute w-80 bg-card border border-border rounded-lg shadow-lg z-50 flex flex-col"
  const panelStyle: React.CSSProperties = isCompact
    ? {}
    : { left: `${effectivePos.x}px`, top: `${effectivePos.y}px` }

  return (
    <div
      data-testid="inspector-panel"
      className={panelClassName}
      style={panelStyle}
    >
      <div
        data-testid="inspector-drag-handle"
        className="flex items-center justify-between px-3 py-2 border-b border-border cursor-move bg-muted rounded-t-lg"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
      >
        <span className="text-xs font-semibold text-foreground">{headerLabel}</span>
        <button
          type="button"
          aria-label="Close inspector"
          onClick={close}
          className="text-muted-foreground hover:text-foreground"
        >
          <X size={14} strokeWidth={1.7} />
        </button>
      </div>
      <div className="flex flex-col max-h-[60vh] overflow-auto">
        {content === 'abox' ? (
          // Phase 050 D-050-03a — ABox properties panel body.
          <PropertiesPanel />
        ) : (
          // PR4 PropertyMetadataRegistry-driven body — preserved byte-
          // identical for the metadata path (existing surface stays usable).
          <div className="flex flex-col gap-2 p-3">
            {properties.length === 0 ? (
              <div className="text-xs text-muted-foreground">No metadata for {componentType}</div>
            ) : (
              properties.map((prop) => (
                <div key={prop.key} className="flex flex-col gap-1">
                  <label className="text-[10px] uppercase text-muted-foreground tracking-wide">
                    {prop.label || prop.key}
                  </label>
                  <div className="text-xs text-foreground">({prop.type})</div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  )
}
